<template>
  <div id="app">
    <MetaHead />
    <component :is="'script'" type="application/ld+json">{{ breadcrumbSchema }}</component>
    <header>
      <nav class="container">
        <div class="logo">
          <img :src="locale === 'de' ? logoBlack : logoBlack" alt="Data Do GmbH - Agentic AI and Data Engineering Agency" class="logo-img" />
        </div>
        <button class="hamburger" :class="{ open: menuOpen }" @click="menuOpen = !menuOpen" aria-label="Toggle menu">
          <span></span>
          <span></span>
          <span></span>
        </button>
        <div class="nav-links" :class="{ open: menuOpen }">
          <router-link :to="{ name: 'home', hash: '#services' }" @click="menuOpen = false">{{ $t('nav.services') }}</router-link>
          <router-link :to="{ name: 'home', hash: '#tech-stack' }" @click="menuOpen = false">{{ $t('nav.techStack') }}</router-link>
          <router-link :to="{ name: 'home', hash: '#case-studies' }" @click="menuOpen = false">{{ $t('nav.caseStudies') }}</router-link>
          <router-link to="/share" @click="menuOpen = false">{{ $t('nav.shareSecretNav') }}</router-link>
          <router-link :to="{ name: 'home', hash: '#contact' }" class="cta-nav-button" @click="menuOpen = false">{{ $t('nav.bookCall') }}</router-link>
          <div class="lang-switcher">
            <button @click="setLocale('en')" :class="{ active: locale === 'en' }">EN</button>
            <button @click="setLocale('de')" :class="{ active: locale === 'de' }">DE</button>
          </div>
        </div>
      </nav>
    </header>

    <main>
      <router-view />
    </main>

    <footer>
      <div class="container footer-content">
        <nav class="footer-sitemap" aria-label="Site Map">
          <h3 class="footer-heading">{{ $t('nav.services') }}</h3>
          <ul class="sitemap-links">
            <li><router-link :to="{ name: 'home', hash: '#services' }">{{ $t('services.cardA.title') }}</router-link></li>
            <li><router-link :to="{ name: 'home', hash: '#services' }">{{ $t('services.cardB.title') }}</router-link></li>
            <li><router-link :to="{ name: 'home', hash: '#services' }">{{ $t('services.cardC.title') }}</router-link></li>
          </ul>
          <h3 class="footer-heading">{{ $t('nav.caseStudies') }}</h3>
          <ul class="sitemap-links">
            <li><router-link :to="{ name: 'home', hash: '#case-studies' }">Enterprise RAG Implementation</router-link></li>
            <li><router-link :to="{ name: 'home', hash: '#case-studies' }">Global DataOps Automation</router-link></li>
          </ul>
          <h3 class="footer-heading">{{ $t('nav.about') }}</h3>
          <ul class="sitemap-links">
            <li><router-link :to="{ name: 'home', hash: '#about' }">{{ $t('about.title') }}</router-link></li>
            <li><router-link :to="{ name: 'home', hash: '#team' }">{{ $t('team.title') }}</router-link></li>
            <li><router-link :to="{ name: 'home', hash: '#contact' }">{{ $t('nav.contact') }}</router-link></li>
          </ul>
          <h3 class="footer-heading">Resources</h3>
          <ul class="sitemap-links">
            <li><router-link to="/enterprise-rag-book">{{ $t('enterpriseRAG.footerLink') }}</router-link></li>
            <li><router-link to="/share">{{ $t('nav.shareSecret') }}</router-link></li>
            <li><router-link :to="{ name: 'home', hash: '#blog' }">{{ $t('blog.title') || 'Deep-Dives' }}</router-link></li>
            <li><router-link :to="{ name: 'home', hash: '#tech-stack' }">{{ $t('nav.techStack') }}</router-link></li>
          </ul>
        </nav>
        <p class="copyright">{{ $t('footer.copy') }}</p>
      </div>
      <div class="compliance-strip">
        <div class="container compliance-content">
          <span class="gdpr-badge">🛡️ {{ $t('footer.gdprBadge') }}</span>
          <div class="legal-links">
            <router-link :to="{ name: 'home', hash: '#imprint' }">{{ $t('footer.impressum') }}</router-link>
            <router-link :to="{ name: 'privacy' }">{{ $t('footer.datenschutz') }}</router-link>
          </div>
        </div>
      </div>
    </footer>
    <CookieConsent />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import logoBlack from './assets/logo_black.svg'
import MetaHead from './components/MetaHead.vue'
import CookieConsent from './components/CookieConsent.vue'

const { locale } = useI18n()
const menuOpen = ref(false)

const setLocale = (lang) => {
  locale.value = lang
}

const breadcrumbSchema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://data-do.com/" },
    { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://data-do.com/#services" },
    { "@type": "ListItem", "position": 3, "name": "Case Studies", "item": "https://data-do.com/#case-studies" },
    { "@type": "ListItem", "position": 4, "name": "About", "item": "https://data-do.com/#about" }
  ]
})
</script>

<style>
/* Global Styles for Accessibility and Speed */
:root {
  --primary-color: #0055ff;
  --primary-dark: #0044cc;
  --primary-light: #e2f1ff;
  --accent-color: #0055ff;
  --accent-dark: #0044cc;
  --accent-light: #e2f1ff;
  --text-color: #1a1a1a;
  --bg-color: #f9f9f9;
  --gray-light: #f5f5f5;
  --white: #ffffff;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--text-color);
  line-height: 1.6;
  background-color: var(--bg-color);
}

h1, h2, h3, h4, h5, h6 {
  font-family: "JetBrains Mono", monospace;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

header {
  padding: 20px 0;
  background-color: var(--white);
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

header nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo-img {
  height: 40px;
  display: block;
}

.nav-links {
  display: flex;
  align-items: center;
}

.nav-links a {
  margin-left: 30px;
  text-decoration: none;
  color: var(--text-color);
  font-weight: 600;
  transition: color 0.3s;
}

.nav-links a:hover {
  color: var(--primary-color);
}

.lang-switcher {
  margin-left: 30px;
  display: flex;
  gap: 10px;
}

.lang-switcher button {
  background: none;
  border: 1px solid var(--primary-color);
  color: var(--primary-color);
  padding: 4px 8px;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: bold;
  border-radius: 4px;
  transition: all 0.3s;
}

.lang-switcher button.active {
  background-color: var(--primary-color);
  color: white;
}

footer {
  padding: 60px 0;
  background-color: #1d1d1d;
  color: white;
  text-align: center;
}

.footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.footer-links {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.footer-link {
  color: white;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.3s;
  opacity: 0.8;
}

.footer-link:hover {
  color: var(--accent-color);
  opacity: 1;
}

.footer-sitemap {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  text-align: left;
  width: 100%;
  margin-bottom: 30px;
}

.footer-heading {
  font-size: 0.95rem;
  font-weight: 700;
  margin-bottom: 10px;
  color: white;
  opacity: 0.9;
}

.sitemap-links {
  list-style: none;
  padding: 0;
  margin: 0 0 10px 0;
}

.sitemap-links li {
  margin-bottom: 6px;
}

.sitemap-links a {
  color: white;
  text-decoration: none;
  font-size: 0.85rem;
  opacity: 0.7;
  transition: opacity 0.3s;
}

.sitemap-links a:hover {
  opacity: 1;
  text-decoration: underline;
}

.copyright {
  font-size: 0.9rem;
  opacity: 0.7;
}

/* CTA Nav Button */
.cta-nav-button {
  background-color: var(--primary-color);
  color: white !important;
  padding: 8px 18px;
  border-radius: 6px;
  font-weight: 700;
  transition: background-color 0.3s;
}

.cta-nav-button:hover {
  background-color: var(--primary-dark);
}

/* Compliance Footer Strip */
.compliance-strip {
  border-top: 1px solid rgba(255, 255, 255, 0.15);
  padding: 20px 0;
  margin-top: 30px;
}

.compliance-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 15px;
}

.gdpr-badge {
  font-weight: 700;
  font-size: 0.9rem;
  opacity: 0.9;
}

.legal-links {
  display: flex;
  gap: 20px;
}

.legal-links a {
  color: white;
  text-decoration: none;
  font-size: 0.85rem;
  opacity: 0.8;
  transition: opacity 0.3s;
}

.legal-links a:hover {
  opacity: 1;
  text-decoration: underline;
}

/* Accessibility: Focus styles */
a:focus, button:focus, input:focus {
  outline: 3px solid var(--accent-color);
  outline-offset: 2px;
}

/* Hamburger button */
.hamburger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  z-index: 1001;
}
.hamburger span {
  display: block;
  width: 25px;
  height: 3px;
  background-color: var(--text-color);
  border-radius: 2px;
  transition: transform 0.3s, opacity 0.3s;
}
.hamburger.open span:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
}
.hamburger.open span:nth-child(2) {
  opacity: 0;
}
.hamburger.open span:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
}

@media (max-width: 768px) {
  .hamburger {
    display: flex;
  }
  .nav-links {
    display: none;
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--white);
    padding: 20px;
    box-shadow: 0 4px 10px rgba(0,0,0,0.1);
    align-items: center;
    gap: 15px;
  }
  .nav-links.open {
    display: flex;
  }
  .nav-links a {
    margin-left: 0;
    font-size: 1.1rem;
  }
  .lang-switcher {
    margin-left: 0;
    margin-top: 10px;
  }
  header nav {
    position: relative;
  }
  footer {
    padding: 40px 20px;
  }
  .footer-links {
    flex-direction: column;
    align-items: center;
  }
  .footer-sitemap {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
